import { PropertyValueMap, TemplateResult, html, CSSResultGroup } from 'lit';
import { property, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { FormController } from '../../controllers/form';
import { SlotController } from '../../controllers/slot-controller';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import type {
  ButtonSize,
  ButtonToggle,
  ButtonType,
  ButtonVariant,
  FormDataContentType,
  FormRequestType,
  FormTargetType,
  IconPosition,
  TargetType
} from './button.model';
import styles from './button.styles';

/**
 * @slot before - The component before content
 * @slot default - The component content
 * @slot after - The component after content
 * @slot custom-link - The component's custom link slot
 * @csspart button - The component's button element
 * @csspart base - The component's base wrapper.
 * @csspart before-wrapper - The component's before slot wrapper.
 * @csspart part-before - The component's before slot.
 * @csspart text - The component's text content.
 * @csspart part-default - The component's default slot.
 * @csspart after-wrapper - The component's after slot wrapper.
 * @csspart part-after - The component's after slot.
 * @csspart part-custom-link - The component's custom link slot
 */
export class BlocksButton extends BlocksElement {
  /**
   * Add a static formAssociated property to custom element class.
   * This tells the browser to treat the element like a form control
   */
  static formAssociated = true;static styles: CSSResultGroup = styles;

  protected formController = new FormController(this);
  /**
   * aria-controls attribute on the button
   * 1) Used for items like the buttons attached drawers
   */
  @property({ type: String })
  ariaControls: string | undefined;

  /**
   * Style variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**primary** renders the button used for primary actions</li>
   * <li>**contrast** renders the button used for more contrast</li>
   * <li>**danger** renders the button used for caution actions</li>
   * <li>**ghost** renders a button that looks like a text link</li>
   * <li>**ghost-danger** renders the ghost button used for caution actions</li>
   * <li>**inline** renders the button that looks like a text link with underline</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String, reflect: true })
  variant: ButtonVariant;

  /**
   * Disabled attribute
   * Since formAssociated is true, cat-button acts like a (form-associated custom element)[https://html.spec.whatwg.org/#form-associated-custom-element].
   * So, if disabled attribute is present on the element (either true or false), click events attached are prevented.
   * So, reflect is added to remove disabled attribute when disabled property is false.
   */
  @property({ type: Boolean, reflect: true })
  disabled: boolean | undefined;

  /**
   * Indicates whether have Menu or not
   */
  @property({ type: Boolean })
  hasPopup: boolean | undefined;

  /**
   * Target attribute for a link (i.e. set to _blank to open in new tab)
   * - **_blank** yields a link that opens in a new tab
   * - **_self** yields a link that loads the URL into the same browsing context as the current one. This is the default behavior
   * - **_parent** yields a link that loads the URL into the parent browsing context of the current one.
   *   If there is no parent, this behaves the same way as _self
   * - **_top** yields a link that loads the URL into the top-level browsing context. If there is no parent, this behaves the same way as _self.
   */
  @property({ type: String })
  target: TargetType;

  /**
   * URL if this is an <a> element - this swaps <button> for <a>
   */
  @property({ type: String })
  href: string | undefined;

  /**
   * Indicates the name when submitted with form data.
   */
  @property({ type: String })
  name: string | undefined;

  /**
   * Indicates the aria label to apply to the button.
   */
  @property({ type: String })
  label: string | undefined;

  /**
   * Indicates the aria current to apply to the button.
   */
  @property({ type: Boolean })
  currentStateIndicator: boolean | undefined;

  /**
   * Indicates the value associated with the name when submitted with form data.
   */
  @property({ type: String })
  value: string | undefined;

  /**
   * Indicates that the button should have input focus when the page loads.
   */
  @property({ type: Boolean, reflect: true })
  autoFocus: boolean | undefined;

  /**
   * Indicates this button is a toggle button and whether it is pressed or not.
   */
  @property({ type: String })
  pressed: ButtonToggle;

  /**
   * Indicates this button is a toggle button and whether it is pressed or not.
   */
  @property({ type: Boolean })
  expanded: boolean | undefined;

  /**
   * Size variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**xs** decreases overall size for icon only buttons of 24px height</li>
   * <li>**sm** decreases overall size for buttons of 32px height</li>
   * <li>**md** increases overall size for buttons of 40px height</li>
   * <li>**lg** increases overall size for buttons of 56px height</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String, reflect: true })
  size: ButtonSize = 'md';

  /**
   * Visually hide button text (but text is still accessible to assistive technology)
   * 1) Use this for icon-only buttons for accessibility
   */
  @property({ type: Boolean, reflect: true })
  hideText = false;

  /**
   * Inverted variant
   * 1) Used for dark backgrounds
   */
  @property({ type: Boolean })
  inverted: boolean | undefined;

  /**
   * Full width button
   */
  @property({ type: Boolean, reflect: true })
  fullWidth: boolean | undefined;

  /**
   * Type of button
   */
  @property({ type: String })
  type: ButtonType = 'button';

  /**
   * Icon position
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**top** positions the icon above the text</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  iconPosition: IconPosition;

  /**
   * Causes the browser to treat the linked URL as a download.
   * Can only works when href presents
   */
  @property({ type: String })
  download: string | undefined;

  /**
   * Used to prevent HTML semantic button from being sequentially focusable. If tabIndex is set on host element,
   * host also gets focus on android screen reader mode. To prevent it, this property can be used.
   * @ignore
   */
  @property({ type: Boolean })
  skipTab = false;

  /**
   * Overrides the action attribute of the button's form owner. Does nothing if there is no form owner.
   */
  @property({ attribute: 'formaction', type: String }) formAction: string | undefined;

  /** Override the form owner's `enctype` attribute.  */
  @property({ attribute: 'formenctype', type: String })
  formEnctype: FormDataContentType;

  /**Used to override the method attribute of the button's form owner  */
  @property({ attribute: 'formmethod', type: String }) formMethod: FormRequestType;

  /**  If this attribute is specified, it overrides the novalidate attribute of the button's form owner. */
  @property({ attribute: 'formnovalidate', type: Boolean }) formNoValidate: boolean | undefined;

  /** If this attribute is specified, it overrides the target attribute of the button's form owner */
  @property({ attribute: 'formtarget', type: String }) formTarget: FormTargetType;

  //Since TS expects both setter and getter type to be same adding this ts-ignore
  @property({ type: String })
  set form(val: string) {
    this._form = val;
  }

  /**
   * Associates the control with a form element
   */
  //Since TS expects both setter and getter type to be same adding any type surpass ts error
  get form(): HTMLFormElement | string {
    return (this.internals_?.form ?? this.formController.form) as HTMLFormElement;
  }

  @query('.cat-c-button') private buttonEl!: HTMLInputElement;

  protected slots = new SlotController(this, 'before', null, 'custom-link', 'after');

  private get hasCustomLinkSlot(): boolean {
    return this.slots.hasSlotted('custom-link');
  }

  /**
   * This attribute lets you associate <button> elements to <form>s anywhere in the document, not just inside a <form>. It can also override an ancestor <form> element.
   */
  //To suppress unused property warning in Typescript added ts-ignore
  // @ts-ignore
  private _form: string;

  private internals_ = this.attachInternals?.();

  /**
   * Connected callback lifecycle
   */
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.handleHostClick);
  }

  /**
   * disConnected callback lifecycle
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.handleHostClick);
  }

  protected updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    if (_changedProperties.has('autoFocus') && this.autoFocus) {
      this.focus();
    }
  }

  override focus(): void {
    this.buttonEl?.focus();
  }

  /**
   * when we click on button which has type=submit trigger requestSubmit on closest form element in
   * order to invoke submit event on form element
   *
   * 1) If the submitter button has a name attribute, its data will be included in the form submission.
   */
  private handleClick(): void {
    if (this.type === 'reset') {
      this.formController.submit(this.type);
    } else if (this.type === 'submit') {
      /**1 */
      if (this.name && this.value) {
        this.internals_?.setFormValue(this.value);
      }
      const submitter = this.shadowRoot?.querySelector('button')?.cloneNode(true) as HTMLElement;
      this.formController.submit(this.type, submitter);
    }
  }

  /**
   * Prevent the click event from being emitted when the button is disabled
   */
  private handleHostClick = (event: MouseEvent) => {
    if (this.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  /**
   * Handle on keydown/click
   * 1) If key selected is enter/clicked, navigate to the custom link
   */

  private handleCustomLinkKeyEvent(e: KeyboardEvent | MouseEvent) {
    if (this.hasCustomLinkSlot) {
      if ((e.type === 'click' || (e instanceof KeyboardEvent && e.key === KEYS.ENTER)) && this._children) {
        this._children[0].click();
      }
    }
  }

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-button', {
      'cat-c-button--primary': this.variant === 'primary',
      'cat-c-button--contrast': this.variant === 'contrast',
      'cat-c-button--ghost': this.variant === 'ghost',
      'cat-c-button--danger': this.variant === 'danger',
      'cat-c-button--ghost-danger': this.variant === 'ghost-danger',
      'cat-c-button--inline': this.variant === 'inline',
      'cat-c-button--xs': this.size === 'xs',
      'cat-c-button--sm': this.size === 'sm',
      'cat-c-button--md': this.size === 'md',
      'cat-c-button--lg': this.size === 'lg',
      'cat-c-button--full-width': this.fullWidth === true,
      'cat-c-button--icon-only': this.hideText === true,
      'cat-c-button--inverted': this.inverted === true,
      'cat-c-button--icon-top': this.iconPosition === 'top',
      'cat-c-button--has-before': this.slots.hasSlotted('before'),
      'cat-c-button--has-after': this.slots.hasSlotted('after')
    });

    if (this.href) {
      return html`
        <a
          part="base"
          href="${ifDefined(this.href)}"
          role="button"
          class="${componentClassName}"
          aria-disabled=${this.disabled ? 'true' : 'false'}
          ?disabled=${this.disabled}
          tabindex=${this.disabled ? -1 : 0}
          download=${ifDefined(this.download ? this.download : undefined)}
          aria-label=${ifDefined(this.label)}
          aria-pressed=${ifDefined(this.pressed)}
          aria-haspopup=${ifDefined(this.hasPopup)}
          aria-expanded=${ifDefined(this.expanded)}
          aria-controls=${ifDefined(this.ariaControls)}
          target=${ifDefined(this.target)}
        >
          <span part="before-wrapper" class="cat-c-button__before"><slot part="part-before" name="before"></slot></span>
          <span part="text" class="${this.hideText ? 'cat-u-is-vishidden cat-c-button__text' : 'cat-c-button__text'}"
            ><slot part="part-default"></slot
          ></span>
          <span part="after-wrapper" class="cat-c-button__after"><slot part="part-after" name="after"></slot></span>
        </a>
      `;
    } else if (this.hasCustomLinkSlot) {
      return html`
        <div
          part="base"
          role="button"
          class="${componentClassName}"
          aria-disabled=${this.disabled ? 'true' : 'false'}
          ?data-disabled=${this.disabled}
          tabindex=${this.disabled ? -1 : 0}
          aria-label=${ifDefined(this.label)}
          aria-pressed=${ifDefined(this.pressed)}
          aria-expanded=${ifDefined(this.expanded)}
          aria-haspopup=${ifDefined(this.hasPopup)}
          aria-controls=${ifDefined(this.ariaControls)}
          @click=${this.handleCustomLinkKeyEvent}
          @keydown=${this.handleCustomLinkKeyEvent}
        >
          <slot part="part-custom-link" name="custom-link" tabindex=${-1}></slot>
        </div>
      `;
    } else {
      return html`
        <button
          part="base button"
          @click=${this.handleClick}
          class="${componentClassName}"
          .type=${this.type}
          tabindex=${ifDefined(this.skipTab ? '-1' : undefined)}
          value=${ifDefined(this.value)}
          name=${ifDefined(this.name)}
          formenctype=${ifDefined(this.formEnctype)}
          formmethod=${ifDefined(this.formMethod)}
          ?formnovalidate=${this.formNoValidate}
          formtarget=${ifDefined(this.formTarget)}
          formaction=${ifDefined(this.formAction)}
          aria-current=${ifDefined(this.currentStateIndicator)}
          aria-label=${ifDefined(this.label)}
          ?autofocus=${this.autoFocus}
          ?disabled=${this.disabled}
          aria-expanded=${ifDefined(this.expanded)}
          aria-haspopup=${ifDefined(this.hasPopup)}
        >
          <span part="before-wrapper" class="cat-c-button__before"><slot part="part-before" name="before"></slot></span>
          <span part="text" class="${this.hideText ? 'cat-u-is-vishidden cat-c-button__text' : 'cat-c-button__text'}"
            ><slot part="part-default"></slot
          ></span>
          <span part="after-wrapper" class="cat-c-button__after"><slot part="part-after" name="after"></slot></span>
        </button>
      `;
    }
  }
}

if (customElements.get('cat-button') === undefined) {
  customElements.define('cat-button', BlocksButton);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-button': BlocksButton;
  }
}
